import { API_BASE_URL } from './api';
import { authService } from './authService';

export interface AdminUserDto {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  lastLoginAt?: string;
  hasChatAccess: boolean;
  hasFlashcardAccess: boolean;
  hasApneaAccess: boolean;
  allowedDeckIds: string[];
  isAdmin: boolean;
  hasSuuntoConnection: boolean;
}

export interface AdminUserListResponse {
  users: AdminUserDto[];
  nextPageToken?: string;
  totalReturned: number;
}

export interface UpdateAccessRequest {
  hasChatAccess: boolean;
  hasFlashcardAccess: boolean;
  hasApneaAccess: boolean;
  allowedDeckIds: string[];
  isAdmin: boolean;
}

class AdminService {
  async getUsers(params?: { search?: string; pageToken?: string; pageSize?: number }): Promise<AdminUserListResponse> {
    const qs = new URLSearchParams();
    if (params?.search) qs.set('search', params.search);
    if (params?.pageToken) qs.set('pageToken', params.pageToken);
    if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
    const response = await fetch(`${API_BASE_URL}/admin/users?${qs}`, {
      headers: { ...authService.getAuthHeaders() }
    });
    if (!response.ok) throw new Error(`Failed to fetch users: ${response.status}`);
    return response.json();
  }

  async getUser(userId: string): Promise<AdminUserDto> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
      headers: { ...authService.getAuthHeaders() }
    });
    if (!response.ok) throw new Error(`Failed to fetch user: ${response.status}`);
    return response.json();
  }

  async getDecks(): Promise<{ deckId: string; deckName: string }[]> {
    const response = await fetch(`${API_BASE_URL}/admin/decks`, {
      headers: { ...authService.getAuthHeaders() }
    });
    if (!response.ok) throw new Error(`Failed to fetch decks: ${response.status}`);
    return response.json();
  }

  async setPassword(userId: string, newPassword: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/password`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
      body: JSON.stringify({ newPassword })
    });
    if (!response.ok) throw new Error(`Failed to set password: ${response.status}`);
  }

  async deleteUser(userId: string): Promise<Record<string, number>> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
      method: 'DELETE',
      headers: { ...authService.getAuthHeaders() }
    });
    if (!response.ok) throw new Error(`Failed to delete user: ${response.status}`);
    const data = await response.json();
    return data.deleted ?? {};
  }

  async disconnectSuunto(userId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/suunto-connection`, {
      method: 'DELETE',
      headers: { ...authService.getAuthHeaders() }
    });
    if (!response.ok) throw new Error(`Failed to disconnect Suunto: ${response.status}`);
  }

  async updateAccess(userId: string, access: UpdateAccessRequest): Promise<AdminUserDto> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/access`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
      body: JSON.stringify(access)
    });
    if (!response.ok) throw new Error(`Failed to update access: ${response.status}`);
    return response.json();
  }
}

export const adminService = new AdminService();

export interface MediaCleanupResult {
  dryRun: boolean;
  objectsScanned: number;
  objectsInUse: number;
  objectsTooRecent: number;
  orphans: number;
  orphanBytes: number;
  deleted: number;
  hitRunLimit: boolean;
  sampleOrphanKeys: string[];
}

// Admin only. dryRun=true only reports; the server defaults to a dry run if the flag is omitted.
export const cleanUpMedia = async (dryRun: boolean): Promise<MediaCleanupResult> => {
  const res = await fetch(`${API_BASE_URL}/admin/media-cleanup?dryRun=${dryRun}`, {
    method: 'POST',
    headers: { ...authService.getAuthHeaders() },
  });
  if (!res.ok) {
    let message = 'Media cleanup failed';
    try { message = (await res.json()).error || message; } catch { /* keep the generic message */ }
    throw new Error(message);
  }
  return res.json();
};


// Admin only. Rewrites every diver's region-index rows from their preferences; safe to repeat.
export const rebuildRegionIndex = async (): Promise<{ divers: number; rows: number }> => {
  const res = await fetch(`${API_BASE_URL}/admin/region-index/rebuild`, {
    method: 'POST',
    headers: { ...authService.getAuthHeaders() },
  });
  if (!res.ok) {
    let message = 'Rebuilding the region index failed';
    try { message = (await res.json()).error || message; } catch { /* keep the generic message */ }
    throw new Error(message);
  }
  return res.json();
};

export interface VideoBackfillResult {
  dryRun: boolean;
  videosFound: number;
  alreadyProcessed: number;
  tooRecent: number;
  missingOriginal: number;
  needVideo: number;
  needPoster: number;
  started: number;
  failed: number;
  hitRunLimit: boolean;
  sampleKeys: string[];
}

// Admin only. dryRun=true only reports what would be started; the server defaults to a dry run if the flag is omitted.
export const backfillVideos = async (dryRun: boolean): Promise<VideoBackfillResult> => {
  const res = await fetch(`${API_BASE_URL}/admin/video-backfill?dryRun=${dryRun}`, {
    method: 'POST',
    headers: { ...authService.getAuthHeaders() },
  });
  if (!res.ok) {
    let message = 'Video backfill failed';
    try { message = (await res.json()).error || message; } catch { /* keep the generic message */ }
    throw new Error(message);
  }
  return res.json();
};
